// @flow
import React from 'react';
import { Provider } from 'react-redux';
import ConnectedApp from './presentation/components/ConnectedApp';
import {
  compose,
  lifecycle,
  withState,
  withProps,
  setDisplayName,
} from 'recompose';
import TypographyImporter from '@ux/typography-importer';
import { BreakpointRoot } from '@ux/responsive';
import * as r from 'ramda';
import en from './presentation/locale/en';
import de from './presentation/locale/de';
import composition from './composition';

const themeOverrides = {
  palette: {
    grey01: '#1a1a1a',
    grey02: '#282828',
    grey03: '#333',
    grey04: '#484848',
    grey05: '#666',
    grey06: '#848484',
    grey07: '#909090',
    grey08: '#999',
    grey09: '#ccc',
    grey10: '#eee',
    grey11: '#f2f2f2',
    grey12: '#f7f7f7',
  },
  typography: {

  },
  root: {
  },
};

const themeDefaults = {
  palette: {
    red01: '#cc0000',
    red02: '#f4c7c7',

    yellow01: '#EDD000',
    yellow02: '#FAF1B2',

    green01: '#7FA800',
    green02: '#D8E5B2',
  },
  typography: {
    sizing: 1,
  },
  root: {
    spacing: 1,
    curvature: 0,
  },
};

const PureRoot = ({
  store,
  locale,
  ...rest
}) => {
  if (store == null) {
    return null;
  }

  return (
    <BreakpointRoot>
      <Provider store={store}>
        <TypographyImporter theme={rest.theme}/>
        <ConnectedApp
          locale={locale}
          {...rest}
        />
      </Provider>
    </BreakpointRoot>
  );
};

const resolveApplicationTheme = (
  themes: {
    default: Object,
    forced: Object,
    provided: Object,
  },
) => {
  return r.pipe(
    // Defaults
    (theme) => r.merge(themes.default.root, theme),
    (theme) => r.assoc(
      'palette',
      r.merge(themes.default.palette, theme.palette),
      theme
    ),
    (theme) => r.assoc(
      'typography',
      r.merge(themes.default.typography, theme.typography),
      theme
    ),
    // Overrides
    (theme) => r.merge(theme, themes.forced.root),
    (theme) => r.assoc(
      'palette',
      r.merge(theme.palette, themes.forced.palette),
      theme
    ),
    (theme) => r.assoc(
      'typography',
      r.merge(theme.typography, themes.forced.typography),
      theme
    ),
  )(themes.provided);
};

type LocaleId = 'en' | 'de';
type Locale = {
  id: LocaleId,
  messages: Object,
};

const isDeLocale = r.equals('de');
const resolveApplicationIntlMessages = (
  userLocale: LocaleId,
  locales: {
    en: Locale,
    de: Locale,
    override: Object,
  },
): Object => {
  // The objective here is to provide locale default and an override
  // We do this so that we can keep as much backwards compatibility as possible
  // without this when introducing new features we would be missing messages
  // that have no yet been defined.
  return r.pipe(
    // We want to use the de locale only when the user is de
    (msgs) => r.cond([
      [
        isDeLocale,
        r.always(r.merge(msgs, locales.de.messages)),
      ],
      [ r.T, r.always(msgs) ],
    ])(userLocale),
    // Overrides take prescendant
    // eslint-disable-next-line no-underscore-dangle
    r.merge(r.__, locales.override),
    // Below we always want en default messages, regardless of user locale
  )(locales.en.messages);
};

export const enhance = compose(
  setDisplayName('Root'),
  // withProps(() => ({
  //   locale: 'en',
  // })),
  withState('store', 'setStore', null),
  lifecycle({
    componentWillMount: function() {
      const {
        store,
      } = composition();

      this.props.setStore(store);
    },
  }),
  withProps((props) => ({
    theme: resolveApplicationTheme({
      default: themeDefaults,
      forced: themeOverrides,
      provided: props.theme,
    }),
    messages: resolveApplicationIntlMessages(props.locale, {
      en,
      de,
      override: props.messages,
    }),
  })),
);

export default enhance(PureRoot);

// eslint-disable-next-line no-underscore-dangle
export const __test__ = {
  resolveApplicationTheme,
  resolveApplicationIntlMessages,
};
